import { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import orderBy from 'lodash/orderBy';
import { Table, tailwindOverride } from '@getsynapse/design-system';
import { TaskWithAssignees } from 'types/store/projectTasksList';
import { TaskListFilters } from 'types/store/tasksList';
import { ProjectTasksTableTab } from 'utils/customTypes';
import { TASK_STATUS } from 'utils/constants';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchTaskTypes,
  selectCanFetchTaskTypes,
  selectTaskTypes,
} from 'state/TasksList/allTasks/allTasksSlice';
import useFilterSettings from './hooks/useFilterSettings';
import TableHeader from './TableHeader/TableHeader';
import TableBody from './TableBody/TableBody';
import TableFooter from 'Pages/TasksListPage/components/v2/TasksListTable/TableFooter/TableFooter';
import ActionsTableHeader from './ActionsTableHeader/ActionsTableHeader';
import TableFilters from './TableFilters/TableFilters';
import SkeletonLoader from 'Organisms/TableSkeletonLoader/PageTableLoader';
import BulkActions from '../BulkActions/BulkActions';

interface TasksTableProps {
  tableName: ProjectTasksTableTab;
  tasks: TaskWithAssignees[];
  totalCount?: number;
  canFetchMoreTasks: boolean;
  fetchMoreTasks: () => void;
  reorderTask?: (taskId: string, startIndex: number, endIndex: number) => void;
  canReorderTasks?: boolean;
  isLoading?: boolean;
  filters: TaskListFilters;
  onUpdateFilters: (filters: TaskListFilters) => void;
  onSearchFilterChange: (value: string) => void;
  onExport: () => void;
  refreshTasks: () => void;
  hasAppliedFilters?: boolean;
}

const TasksTable: FC<TasksTableProps> = ({
  tableName,
  tasks,
  totalCount,
  canFetchMoreTasks,
  fetchMoreTasks,
  canReorderTasks = false,
  isLoading = false,
  hasAppliedFilters = false,
  reorderTask,
  filters,
  onUpdateFilters,
  onSearchFilterChange,
  onExport,
  refreshTasks,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const {
    bulkTaskAssign = false,
    bulkTaskDelete = false,
    newTaskTypes = false,
  } = useFlags();

  const thunkDispatch = useAppThunkDispatch();
  const canFetchTaskTypes = useSelector(selectCanFetchTaskTypes);
  const taskTypes = useSelector(selectTaskTypes);

  useEffect(() => {
    if (canFetchTaskTypes) {
      thunkDispatch(fetchTaskTypes(newTaskTypes));
    }
  }, [canFetchTaskTypes, thunkDispatch, newTaskTypes]);

  const typeFilterOptions = useMemo(
    () =>
      orderBy(
        Object.entries(taskTypes).map(([code, name]) => ({
          value: code,
          label: name,
        })),
        'label'
      ),
    [taskTypes]
  );

  const isShowBulkActions = bulkTaskAssign || bulkTaskDelete;

  const showDisabledColumn = useMemo(
    () =>
      tasks.some(
        (task) => task.disabled || task.status === TASK_STATUS.COMPLETED
      ),
    [tasks]
  );

  const isListEmpty = tasks.length === 0;

  const toggleIsDragging = () => {
    setIsDragging(!isDragging);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !reorderTask) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    reorderTask(
      result.draggableId,
      result.source.index,
      result.destination.index
    );
    toggleIsDragging();
  };

  const resetTableScroll = useCallback(() => {
    const element = document.getElementById(tableName) as HTMLElement;
    if (element) {
      element.scrollTop = 0;
    }
  }, [tableName]);

  const handleFiltersUpdate = useCallback(
    (updatedFilters: TaskListFilters) => {
      onUpdateFilters(updatedFilters);
      resetTableScroll();
    },
    [onUpdateFilters, resetTableScroll]
  );

  const handleSearchFilterChange = useCallback(
    (value: string) => {
      onSearchFilterChange(value);
      resetTableScroll();
    },
    [onSearchFilterChange, resetTableScroll]
  );

  const { setFiltersSettings } = useFilterSettings({
    tableName,
    updateFiltersCallback: handleFiltersUpdate,
  });

  return (
    <>
      <TableFilters
        filters={filters}
        onUpdateFilters={setFiltersSettings}
        typeFilterOptions={typeFilterOptions}
      />
      {isLoading && (
        <SkeletonLoader data-testid={`${tableName}__skeleton-loader`} />
      )}
      {!isLoading && (
        <DragDropContext
          onDragStart={toggleIsDragging}
          onDragEnd={handleDragEnd}
        >
          <ActionsTableHeader
            tableName={tableName}
            filters={filters}
            onUpdateFilters={setFiltersSettings}
            onSearchFilterChange={handleSearchFilterChange}
            onExport={onExport}
            taskTypeNames={taskTypes}
          />
          <div className='relative h-table-skeleton-loader'>
            <div
              className={tailwindOverride(
                'w-full',
                'rounded-b border-neutral-lighter-two border border-t-0',
                'overflow-auto',
                {
                  'max-h-centralized-tasks-table': !hasAppliedFilters,
                  'max-h-filtered-centralized-tasks-table': hasAppliedFilters,
                }
              )}
              id={tableName}
            >
              <Table
                className={tailwindOverride('relative', {
                  'max-w-full border-0': !isListEmpty,
                  'w-full': isListEmpty,
                })}
                canSelectRows
                isSelectRowCellSticky={true}
                aria-label={tableName}
                data={{
                  headData: {
                    headCells: [],
                  },
                  rows: [],
                }}
              >
                <TableHeader
                  showDisabledColumn={showDisabledColumn}
                  isListEmpty={tasks.length === 0}
                />
                <TableBody
                  tableName={tableName}
                  tasks={tasks}
                  showDisabledColumn={showDisabledColumn}
                  canFetchMoreTasks={canFetchMoreTasks}
                  fetchMoreTasks={fetchMoreTasks}
                  canReorderTasks={canReorderTasks}
                  isDragging={isDragging}
                  refreshTasks={refreshTasks}
                />
              </Table>
            </div>
            {isShowBulkActions && (
              <BulkActions
                bulkTaskAssignFlag={bulkTaskAssign}
                bulkTaskDeleteFlag={bulkTaskDelete}
              />
            )}
          </div>
          <TableFooter total={totalCount} />
        </DragDropContext>
      )}
    </>
  );
};

export default TasksTable;
